export interface RosterNotes {
  startSit: string[];
  news: string[];
  trades: string[];
  outlook: string;
}

const EMPTY: RosterNotes = {
  startSit: [],
  news: [],
  trades: [],
  outlook: '',
};

export function cleanNoteText(text: string): string {
  return stripCitations(text)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function clipWords(text: string, max = 16): string {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= max) return text;
  return `${words.slice(0, max).join(' ').replace(/[.,;:]+$/, '')}.`;
}

export function firstSentence(text: string): string {
  const cleaned = cleanNoteText(text).replace(/^(start|sit)\.\s+/i, '');
  const match = cleaned.match(/^(.+?[.!?])(?:\s|$)/);
  return clipWords((match?.[1] || cleaned).trim(), 14);
}

export function isRosterDump(text: string): boolean {
  const cleaned = cleanNoteText(text);
  const commas = (cleaned.match(/,/g) || []).length;
  return commas >= 3 && /no start sit|no relevant|so no |surfaced here/i.test(cleaned);
}

export function splitNoteLine(raw: string, knownNames: string[] = []): {
  name: string;
  call: 'Start' | 'Sit' | null;
  body: string;
} {
  const text = cleanNoteText(raw);
  const knownName = knownNames
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .find((name) => text.toLowerCase().startsWith(name.toLowerCase()));
  if (knownName) {
    const afterName = text.slice(knownName.length).replace(/^[\s:.,;-]+/, '');
    const callMatch = afterName.match(/^(Start|Sit)\b[\s:.,;-]*/i);
    return {
      name: knownName,
      call: callMatch ? (/^sit$/i.test(callMatch[1]) ? 'Sit' : 'Start') : null,
      body: firstSentence(callMatch ? afterName.slice(callMatch[0].length) : afterName),
    };
  }

  const namedCall = text.match(/^([A-Z][\w'-]*(?:\s+[A-Z][\w'-]*){0,3})\s+(Start|Sit)\b[\s:.,;-]*(.*)$/i);
  if (namedCall) {
    return {
      name: namedCall[1],
      call: /^sit$/i.test(namedCall[2]) ? 'Sit' : 'Start',
      body: firstSentence(namedCall[3]),
    };
  }

  const named = text.match(/^([A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+){0,3})\s*[:.]\s+(.*)$/);
  if (named) {
    const after = named[2];
    const call = /^\s*start\b/i.test(after) ? 'Start' : /^\s*sit\b/i.test(after) ? 'Sit' : null;
    return { name: named[1], call, body: firstSentence(after) };
  }

  const startSit = text.match(/^(Start|Sit)\s+(.+)$/i);
  if (startSit) {
    const rest = startSit[2];
    const player = rest.match(/^([A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+){0,3})\b(.*)$/);
    return {
      name: player?.[1] || '',
      call: /^sit$/i.test(startSit[1]) ? 'Sit' : 'Start',
      body: firstSentence((player?.[2] || rest).replace(/^[\s.,:-]+/, '')),
    };
  }

  return { name: '', call: null, body: firstSentence(text) };
}

export function compactNoteItems(
  items: string[],
  limit: number,
  knownNames: string[] = []
): ReturnType<typeof splitNoteLine>[] {
  const seen = new Set<string>();
  const compacted: ReturnType<typeof splitNoteLine>[] = [];
  for (const item of items) {
    if (isRosterDump(item)) continue;
    const note = splitNoteLine(item, knownNames);
    const key = (note.name || note.body).toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    compacted.push(note);
    if (compacted.length >= limit) break;
  }
  return compacted;
}

export function outlookItems(outlook: string): ReturnType<typeof splitNoteLine>[] {
  const text = cleanNoteText(outlook).replace(/^-\s*/, '');
  if (!text) return [];
  const parts = text
    .split(/\s+-\s+(?=[A-Z])/)
    .map((part) => part.replace(/^-\s*/, '').trim())
    .filter(Boolean);
  return compactNoteItems(parts.length > 1 ? parts : [text], 5);
}

export function stripCitations(text: string): string {
  return text
    .replace(/\[(?:web|page|file):\d+\]/gi, '')
    .replace(/\[\d+\]/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function agentOutputText(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const data = payload as { output_text?: unknown; output?: unknown };
  if (typeof data.output_text === 'string' && data.output_text.trim()) {
    return data.output_text.trim();
  }
  if (!Array.isArray(data.output)) return '';
  const parts: string[] = [];
  for (const item of data.output) {
    if (!item || typeof item !== 'object') continue;
    const message = item as { type?: string; content?: unknown };
    if (message.type !== 'message' || !Array.isArray(message.content)) continue;
    for (const chunk of message.content) {
      if (!chunk || typeof chunk !== 'object') continue;
      const text = (chunk as { type?: string; text?: string }).text;
      if ((chunk as { type?: string }).type === 'output_text' && typeof text === 'string') {
        parts.push(text);
      }
    }
  }
  return parts.join('\n').trim();
}

function bullets(block: string): string[] {
  return block
    .split('\n')
    .map((line) => stripCitations(line.replace(/^[-*•]\s+/, '').replace(/^\d+[\).]\s+/, '')))
    .filter((line) => line.length > 0 && !/^#+\s/.test(line));
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function extractJsonObject(raw: string): string | null {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidate = (fenced?.[1] || raw).trim();
  if (candidate.startsWith('{') && candidate.endsWith('}')) return candidate;
  const start = candidate.indexOf('{');
  const end = candidate.lastIndexOf('}');
  if (start >= 0 && end > start) return candidate.slice(start, end + 1);
  return null;
}

export function notesFromAgentText(raw: string): RosterNotes {
  const jsonish = extractJsonObject(raw);
  if (jsonish) {
    try {
      const data = JSON.parse(jsonish) as {
        startSit?: unknown;
        news?: unknown;
        trades?: unknown;
        outlook?: unknown;
      };
      const notes = {
        startSit: asStringList(data.startSit).map(stripCitations),
        news: asStringList(data.news).map(stripCitations),
        trades: asStringList(data.trades).map(stripCitations),
        outlook: stripCitations(typeof data.outlook === 'string' ? data.outlook : ''),
      };
      if (notes.startSit.length || notes.news.length || notes.trades.length || notes.outlook) {
        return notes;
      }
    } catch {
      // fall through to markdown headers
    }
  }
  return parseRosterNotes(raw);
}

export function parseRosterNotes(raw: string): RosterNotes {
  if (!raw.trim()) return { ...EMPTY };
  const normalized = raw.replace(/\r\n/g, '\n');
  const sections: Record<string, string> = {};
  let current = 'outlook';
  const chunks: string[] = [];

  for (const line of normalized.split('\n')) {
    const header = line.replace(/^#+\s*/, '').trim().toLowerCase();
    if (/^start\s*sit$/.test(header) || header === 'start / sit') {
      if (chunks.length) sections[current] = chunks.join('\n');
      current = 'startSit';
      chunks.length = 0;
      continue;
    }
    if (header === 'news') {
      if (chunks.length) sections[current] = chunks.join('\n');
      current = 'news';
      chunks.length = 0;
      continue;
    }
    if (header === 'trades' || header === 'trade') {
      if (chunks.length) sections[current] = chunks.join('\n');
      current = 'trades';
      chunks.length = 0;
      continue;
    }
    if (header === 'outlook') {
      if (chunks.length) sections[current] = chunks.join('\n');
      current = 'outlook';
      chunks.length = 0;
      continue;
    }
    chunks.push(line);
  }
  if (chunks.length) sections[current] = chunks.join('\n');

  return {
    startSit: bullets(sections.startSit || ''),
    news: bullets(sections.news || ''),
    trades: bullets(sections.trades || ''),
    outlook: stripCitations((sections.outlook || '').replace(/\n+/g, ' ')),
  };
}
